import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
// Api
import { REFUND_CANCELLED_TICKET_EVENT_ORDERS } from 'api/ticketing/mutations';
// Types
import {
  RefundCancelledTicketEventOrders,
  RefundCancelledTicketEventOrdersVariables,
} from 'api/ticketing/types/RefundCancelledTicketEventOrders';
// Helpers
import { pluralize } from 'pages/Events/helpers';
import { runTicketingChange } from 'pages/Events/runTicketingChange';

export type BulkRefund = {
  /** Resolves to how many orders the server queued, or null when it failed. */
  startRefunds: (ticketEventId: string) => Promise<number | null>;
  starting: boolean;
};

export const useBulkRefund = (): BulkRefund => {
  const [refundAll, { loading: starting }] = useMutation<
    RefundCancelledTicketEventOrders,
    RefundCancelledTicketEventOrdersVariables
  >(REFUND_CANCELLED_TICKET_EVENT_ORDERS);

  const startRefunds = useCallback(
    async (ticketEventId: string): Promise<number | null> => {
      let started = 0;
      const done = await runTicketingChange(
        async () => {
          const { data } = await refundAll({
            variables: { input: { ticketEventId } },
          });

          started =
            data?.refundCancelledTicketEventOrders.refundableOrders ?? 0;
        },
        () =>
          started
            ? `Refunds started for ${pluralize(
                started,
                'order'
              )}. They show as "Refund pending" within a minute.`
            : 'There are no paid orders left to refund.',
        'Failed to start the refunds'
      );

      return done ? started : null;
    },
    [refundAll]
  );

  return { startRefunds, starting };
};
